import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Truck, Plus, X, Printer, PencilSimple, Warning, MagnifyingGlass, Trash,
} from "@phosphor-icons/react";
import StockPartCombobox from "@/components/StockPartCombobox";
import SerialEditor from "@/components/SerialEditor";
import { fmtDate } from "@/lib/dates";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A Packing List row uses only the fields we actually persist — anything the
// combobox tosses in extra (like `stock_id`) is dropped on submit.
const blankPart = () => ({
  name: "", quantity: 1, stock_code: null, group: null, uom: null, notes: "",
  serials: [],
});

export default function PackingListPage() {
  const { user } = useAuth();
  const canCreate = ["storekeeper", "manager"].includes(user.role);
  const canEdit = user.role === "manager";
  const canDelete = user.role === "manager";
  const [lists, setLists] = useState([]);
  const [customers, setCustomers] = useState([]);   // walk-in only
  const [regions, setRegions] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCustomer !== "all") params.customer_id = filterCustomer;
      if (search.trim()) params.q = search.trim();
      const [pl, cust, reg] = await Promise.all([
        api.get("/packing-lists", { params }),
        api.get("/customers", { params: { active_only: true, type: "walkin" } }),
        api.get("/regions"),
      ]);
      setLists(pl.data || []);
      setCustomers(cust.data || []);
      setRegions(reg.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCustomer]);
  useEffect(() => {
    // Debounce search to avoid a request per keystroke.
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [search]);

  const totalUnits = useMemo(
    () => lists.reduce((s, pl) => s + (pl.parts || []).reduce((x, p) => x + (p.quantity || 0), 0), 0),
    [lists],
  );

  const openPdf = (pl) => window.open(`${API_BASE}/packing-lists/${pl.id}/receipt.pdf`, "_blank", "noopener");

  const deletePl = async (pl) => {
    const parts = pl.parts || [];
    const partsText = parts.slice(0, 3).map((p) => `${p.name}×${p.quantity}`).join(", ") + (parts.length > 3 ? "…" : "");
    if (!window.confirm(
      `Delete ${pl.pl_no} to ${pl.customer_name}?\n\n` +
      `${partsText}\n\n` +
      `Stock will be RETURNED to main inventory. This cannot be undone.`
    )) return;
    try {
      await api.delete(`/packing-lists/${pl.id}`);
      toast.success(`${pl.pl_no} deleted · stock returned to main`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="border-b border-neutral-200 pt-10">
        <div className="flex items-start justify-between px-10 pb-6">
          <div>
            <div className="label-eyebrow">Deliveries</div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2 flex items-center gap-3">
              <Truck size={36} weight="duotone" className="text-emerald-700" /> Packing Lists
            </h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-lg">
              Ship-out slips for walk-in customer deliveries. Auto-deducts main stock on save
              and prints a PL voucher for the driver.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="label-eyebrow">Total PLs</div>
              <div className="font-display text-3xl" data-testid="pl-count">{lists.length}</div>
              <div className="text-[10px] font-mono text-neutral-500 uppercase tracking-wider">{totalUnits} units shipped</div>
            </div>
            {canCreate && (
              <Button
                onClick={() => setShowCreate(true)}
                data-testid="new-pl-btn"
                className="h-10 rounded-none bg-emerald-700 text-white hover:bg-emerald-800"
              >
                <Plus size={16} weight="bold" className="mr-2" /> Create PL
              </Button>
            )}
          </div>
        </div>
        <div className="px-10 pb-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PL# or customer name…"
              className="h-10 pl-9 rounded-none border-neutral-300 focus-visible:border-neutral-900 focus-visible:ring-0"
              data-testid="pl-search"
            />
          </div>
          <Select value={filterCustomer} onValueChange={setFilterCustomer}>
            <SelectTrigger data-testid="pl-filter-customer" className="h-10 w-64 rounded-none border-neutral-300"><SelectValue placeholder="Customer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All walk-in customers</SelectItem>
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="p-10">
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="pl-table">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">PL #</th>
                <th className="px-4 py-3 label-eyebrow">Date</th>
                <th className="px-4 py-3 label-eyebrow">Customer</th>
                <th className="px-4 py-3 label-eyebrow">Address</th>
                <th className="px-4 py-3 label-eyebrow">Parts (qty)</th>
                <th className="px-4 py-3 label-eyebrow">Shipment</th>
                <th className="px-4 py-3 label-eyebrow">Prepared by</th>
                <th className="px-4 py-3 label-eyebrow w-24 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-400 text-xs">Loading…</td></tr>}
              {!loading && lists.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-400">
                  {canCreate ? "No packing lists yet — click 'Create PL' to start." : "No packing lists yet."}
                </td></tr>
              )}
              {!loading && lists.map((pl) => {
                const parts = pl.parts || [];
                const qty = parts.reduce((s, p) => s + (p.quantity || 0), 0);
                const partsSummary = parts.slice(0, 3).map((p) => `${p.name}×${p.quantity}`).join(", ");
                const more = parts.length > 3 ? ` +${parts.length - 3} more` : "";
                const dims = [
                  pl.weight_kg && `${pl.weight_kg} kg`,
                  (pl.length_cm || pl.width_cm || pl.height_cm)
                    && `${pl.length_cm ?? "—"} × ${pl.width_cm ?? "—"} × ${pl.height_cm ?? "—"} cm`,
                ].filter(Boolean).join(" · ");
                return (
                  <tr key={pl.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`pl-row-${pl.id}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-emerald-800">{pl.pl_no}</td>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-600">
                      {fmtDate(pl.created_at)}
                    </td>
                    <td className="px-4 py-3 font-medium">{pl.customer_name}</td>
                    <td className="px-4 py-3 text-xs text-neutral-600 max-w-[200px] truncate" title={pl.customer_address || "—"}>
                      {pl.customer_address || <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{partsSummary}{more}</div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">
                        {parts.length} line{parts.length === 1 ? "" : "s"} · {qty} units
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-600">
                      {dims || <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{pl.created_by_name || "—"}</div>
                      {pl.updated_by_name && (
                        <div className="text-[10px] font-mono text-neutral-500 mt-0.5" title={pl.updated_at || ""}>
                          Edited by {pl.updated_by_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => openPdf(pl)}
                          data-testid={`pl-print-${pl.id}`}
                          className="p-1.5 border border-neutral-300 hover:border-emerald-700 hover:bg-emerald-700 hover:text-white"
                          title="Print PL"
                        >
                          <Printer size={13} weight="bold" />
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => setEditing(pl)}
                            data-testid={`pl-edit-${pl.id}`}
                            className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                            title="Edit (Manager)"
                          >
                            <PencilSimple size={13} weight="bold" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => deletePl(pl)}
                            data-testid={`pl-delete-${pl.id}`}
                            className="p-1.5 border border-neutral-300 hover:border-red-600 hover:bg-red-600 hover:text-white"
                            title="Delete PL — returns stock to main (Manager only)"
                          >
                            <Trash size={13} weight="bold" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {canCreate && (
        <PackingListDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          customers={customers}
          regions={regions}
          onSaved={(pl) => {
            setShowCreate(false);
            load();
            if (pl?.id) window.open(`${API_BASE}/packing-lists/${pl.id}/receipt.pdf`, "_blank", "noopener");
          }}
        />
      )}
      {canEdit && editing && (
        <PackingListDialog
          open={!!editing}
          editing={editing}
          onClose={() => setEditing(null)}
          customers={customers}
          regions={regions}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


// --------------------------------------------------------------------------- //
// Create + Edit dialog (same shape; PATCH when `editing` present, POST otherwise).
// --------------------------------------------------------------------------- //
function PackingListDialog({ open, editing, onClose, customers, regions, onSaved }) {
  const isEdit = !!editing;
  const [customerId, setCustomerId] = useState("");
  const [region, setRegion] = useState("");
  const [weight, setWeight] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([blankPart()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setCustomerId(editing.customer_id || "");
      setRegion(editing.region || "");
      setWeight(editing.weight_kg ?? "");
      setLength(editing.length_cm ?? "");
      setWidth(editing.width_cm ?? "");
      setHeight(editing.height_cm ?? "");
      setNotes(editing.notes || "");
      setItems((editing.parts && editing.parts.length ? editing.parts : [blankPart()]).map((p) => ({
        name: p.name || "", quantity: p.quantity || 1,
        stock_code: p.stock_code || null, group: p.group || null,
        uom: p.uom || null, notes: p.notes || "",
        serials: Array.isArray(p.serials) ? p.serials : [],
      })));
    } else {
      setCustomerId(""); setRegion(""); setWeight(""); setLength("");
      setWidth(""); setHeight(""); setNotes(""); setItems([blankPart()]);
    }
  }, [open, isEdit, editing]);

  const setItem = (i, k, v) => setItems(items.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const pickItem = (i, picked) => setItems(items.map((p, idx) => idx === i ? {
    ...p,
    name: picked.name,
    stock_code: picked.stock_code || null,
    group: picked.group || null,
    uom: picked.uom || null,
  } : p));
  const addItem = () => setItems([...items, blankPart()]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!customerId) return toast.error("Pick a customer");
    const cleaned = items
      .filter((p) => (p.name || "").trim())
      .map((p) => ({
        name: p.name.trim(),
        quantity: Math.max(1, Number(p.quantity) || 1),
        stock_code: p.stock_code || null,
        group: p.group || null,
        uom: p.uom || null,
        notes: p.notes || "",
        serials: (p.serials || []).map((s) => String(s).trim()).filter(Boolean),
      }));
    if (cleaned.length === 0) return toast.error("Add at least one part");
    setBusy(true);
    try {
      const payload = {
        customer_id: customerId,
        region: region || null,
        parts: cleaned,
        weight_kg: weight === "" ? null : Number(weight),
        length_cm: length === "" ? null : Number(length),
        width_cm: width === "" ? null : Number(width),
        height_cm: height === "" ? null : Number(height),
        notes: notes || "",
      };
      const res = isEdit
        ? await api.patch(`/packing-lists/${editing.id}`, payload)
        : await api.post("/packing-lists", payload);
      toast.success(isEdit ? `${res.data.pl_no} updated` : `${res.data.pl_no} created`);
      onSaved(res.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const totalQty = items.reduce((s, p) => s + (Number(p.quantity) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-emerald-700 max-h-[90vh] overflow-y-auto" data-testid="pl-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2 text-emerald-800">
            <Truck size={24} weight="duotone" />
            {isEdit ? `Edit ${editing.pl_no}` : "New Packing List"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-emerald-700 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
            {isEdit ? (
              <><b>Manager edit.</b> Any qty change re-balances main stock automatically.</>
            ) : (
              <><b>Ship-out slip.</b> Stock deducts from main immediately, PL prints on save.</>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Walk-in customer <span className="text-red-600 normal-case">*</span></Label>
              <Select value={customerId} onValueChange={setCustomerId} disabled={isEdit}>
                <SelectTrigger data-testid="pl-customer" className="rounded-none border-neutral-400">
                  <SelectValue placeholder="Pick walk-in customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.length === 0
                    ? (
                      <div className="px-2 py-3 text-xs text-neutral-500 max-w-xs">
                        No walk-in customers yet.<br />
                        Go to <b>Customers</b> → <b>Add customer</b> → select <b>Walk-in</b> first.
                      </div>
                    )
                    : customers.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`pl-customer-${c.id}`}>{c.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {isEdit && <div className="text-[10px] font-mono text-neutral-500">Customer locked — create a new PL to change recipient.</div>}
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Region</Label>
              <Select value={region || "__none__"} onValueChange={(v) => setRegion(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="pl-region" className="rounded-none border-neutral-400">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Weight (kg)</Label>
              <Input type="number" min={0} step="0.01" value={weight}
                onChange={(e) => setWeight(e.target.value)}
                data-testid="pl-weight"
                className="rounded-none border-neutral-400 font-mono" placeholder="—" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Length (cm)</Label>
              <Input type="number" min={0} step="0.1" value={length}
                onChange={(e) => setLength(e.target.value)}
                data-testid="pl-length"
                className="rounded-none border-neutral-400 font-mono" placeholder="—" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Width (cm)</Label>
              <Input type="number" min={0} step="0.1" value={width}
                onChange={(e) => setWidth(e.target.value)}
                data-testid="pl-width"
                className="rounded-none border-neutral-400 font-mono" placeholder="—" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Height (cm)</Label>
              <Input type="number" min={0} step="0.1" value={height}
                onChange={(e) => setHeight(e.target.value)}
                data-testid="pl-height"
                className="rounded-none border-neutral-400 font-mono" placeholder="—" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="label-eyebrow">Parts to ship</Label>
              <button type="button" onClick={addItem} data-testid="pl-add-item-btn" className="text-xs font-medium underline underline-offset-4 hover:text-neutral-500">+ Add part</button>
            </div>
            <div className="hidden md:grid grid-cols-12 gap-2 px-1 text-[10px] font-mono uppercase tracking-wider text-neutral-500">
              <div className="col-span-8">Part</div>
              <div className="col-span-3 text-right">Qty</div>
              <div className="col-span-1"></div>
            </div>
            {items.map((p, i) => (
              <div key={i} className="border border-neutral-200 p-2 space-y-2" data-testid={`pl-row-${i}`}>
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-8">
                    <StockPartCombobox
                      value={p}
                      onChange={(picked) => pickItem(i, picked)}
                      testid={`pl-picker-${i}`}
                    />
                  </div>
                  <Input type="number" min={1} value={p.quantity}
                    onChange={(e) => setItem(i, "quantity", e.target.value)}
                    className="col-span-3 rounded-none border-neutral-400 h-9 text-right font-mono"
                    data-testid={`pl-qty-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    disabled={items.length === 1}
                    className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Remove line"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
                <SerialEditor
                  serials={p.serials || []}
                  onChange={(next) => setItem(i, "serials", next)}
                  testidPrefix={`pl-serial-${i}`}
                />
              </div>
            ))}
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="pl-notes"
              className="rounded-none border-neutral-400" placeholder="Optional — e.g. driver name, urgent, fragile…" />
          </div>

          <div className="flex items-center justify-between border-t-2 border-emerald-700 pt-3">
            <div className="label-eyebrow">Total units to ship</div>
            <div className="text-2xl font-display tracking-tight text-emerald-800 font-mono" data-testid="pl-total-qty">
              {totalQty}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy}
            data-testid="pl-submit"
            className="rounded-none bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Create + Print PL")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
